import styles from 'css/src/components/common/mentions/MentionTypeaheadMenu.module.scss';

import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { BLUR_COMMAND, COMMAND_PRIORITY_LOW, FOCUS_COMMAND } from 'lexical';

import { useMenuOpensAbove } from './useMenuOpensAbove';

/**
 * Whether the editor holds focus. The typeahead keeps its menu open across a
 * blur, leaving a menu floating over an input the user has left.
 */
export function useEditorIsFocused(): boolean {
    const [editor] = useLexicalComposerContext();
    const [isFocused, setIsFocused] = React.useState(false);

    React.useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                FOCUS_COMMAND,
                () => {
                    setIsFocused(true);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                BLUR_COMMAND,
                () => {
                    setIsFocused(false);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor]);

    return isFocused;
}

/** The menu scrolls, so arrow keys would otherwise walk the highlight out of view. */
export function useScrollSelectedIntoView(isSelected: boolean) {
    const itemRef = React.useRef<HTMLLIElement | null>(null);
    React.useEffect(() => {
        if (isSelected) {
            itemRef.current?.scrollIntoView({ block: 'nearest' });
        }
    }, [isSelected]);
    return itemRef;
}

interface MentionMenuProps {
    /** Lexical's caret anchor, so the menu tracks the "@" not the input box. */
    anchor: HTMLElement;
    /** Re-measures which way to open as the list grows or shrinks. */
    optionCount: number;
    /** Marks the menu for the editor's Enter/ArrowUp handlers to detect. */
    testAttribute?: string;
    /** Runs when the menu unmounts, however it closed. */
    onClosed?: () => void;
    className?: string;
    children: React.ReactNode;
}

/**
 * A component rather than markup inlined in `menuRenderFn` so it can measure
 * the caret in an effect — `menuRenderFn` runs during render, where it can't.
 */
const MentionMenu: React.FC<MentionMenuProps> = ({
    anchor,
    optionCount,
    testAttribute,
    onClosed,
    className,
    children,
}) => {
    const opensAbove = useMenuOpensAbove(anchor, optionCount);

    // Tied to unmounting rather than to why it closed, so pick, Escape, blur
    // and typing past the trigger are all covered.
    const onClosedRef = React.useRef(onClosed);
    React.useEffect(() => {
        onClosedRef.current = onClosed;
    }, [onClosed]);
    React.useEffect(() => {
        return () => onClosedRef.current?.();
    }, []);

    return (
        <ul
            className={classNames(styles['mention-typeahead-menu'], 'm-l5-0', 'p-l5-0', className, {
                [styles['mention-typeahead-menu--above']]: opensAbove,
            })}
            {...(testAttribute ? { [testAttribute]: '' } : {})}
        >
            {children}
        </ul>
    );
};

interface MentionMenuPortalProps extends Omit<MentionMenuProps, 'anchor'> {
    /** The `anchorElementRef` handed to `menuRenderFn`. */
    anchorRef: React.RefObject<HTMLElement | null>;
    /**
     * Include `useEditorIsFocused()` here: it must be called from the plugin,
     * which outlives the menu, or it misses the focus it needs to observe.
     */
    isOpen: boolean;
}

/**
 * The shared shell for an "@" mention menu, portalled into the caret anchor:
 * positioned on the caret, flipping above it when the input sits too near the
 * bottom of the window. Use as the body of a `menuRenderFn`.
 */
export const MentionMenuPortal: React.FC<MentionMenuPortalProps> = ({
    anchorRef,
    isOpen,
    ...menuProps
}) => {
    if (!anchorRef.current || !isOpen) {
        return null;
    }

    return ReactDOM.createPortal(
        <MentionMenu anchor={anchorRef.current} {...menuProps} />,
        anchorRef.current,
    );
};
